import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import Modal from './Modal';

// Camera-based barcode scanner. The host should pass an `onResult(code)`
// callback — the modal closes itself as soon as a code is captured.
//
// USB / Bluetooth scanners do NOT need this modal: they emulate a keyboard,
// so just focusing the barcode input is enough. This modal is the fallback
// for tablets / phones at the counter that have no physical scanner.
const BarcodeScannerModal = ({ open, onClose, onResult }) => {
  const containerId = 'barcode-scanner-region';
  const scannerRef = useRef(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    let cancelled = false;
    const start = async () => {
      setError(null);
      setStarting(true);
      try {
        const cameras = await Html5Qrcode.getCameras();
        if (cancelled) return;
        if (!cameras || cameras.length === 0) {
          setError('No camera detected. Use a USB barcode scanner — just focus the barcode field and scan.');
          setStarting(false);
          return;
        }
        // Prefer the back/environment camera when available (mobile / tablet).
        const back = cameras.find((c) => /back|rear|environment/i.test(c.label)) || cameras[0];
        const scanner = new Html5Qrcode(containerId, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          back.id,
          { fps: 12, qrbox: { width: 280, height: 160 } },
          (decoded) => {
            // Successful read → bubble up and close.
            try { scanner.pause(true); } catch (_) {}
            onResult?.(String(decoded).trim());
            close();
          },
          () => { /* per-frame error callback — ignored */ },
        );
        setStarting(false);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || 'Could not start the camera. Check the browser permission.');
          setStarting(false);
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      // Tear down camera + DOM cleanly.
      const s = scannerRef.current;
      if (s) {
        s.stop().catch(() => {}).finally(() => {
          try { s.clear(); } catch (_) {}
          scannerRef.current = null;
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    const s = scannerRef.current;
    if (s) {
      s.stop().catch(() => {}).finally(() => {
        try { s.clear(); } catch (_) {}
        scannerRef.current = null;
        onClose?.();
      });
    } else {
      onClose?.();
    }
  };

  return (
    <Modal
      title="Scan barcode" open={open} onClose={close} width={520}
      footer={<button className="btn-ghost" onClick={close}>Close</button>}
    >
      <p style={{ color: 'var(--mute)', margin: '0 0 14px', fontSize: 13.5 }}>
        Hold the product so its barcode fills the frame. Most 1D and 2D codes are supported (EAN-13, UPC, Code-128, QR…).
      </p>
      {error ? (
        <div className="notice">{error}</div>
      ) : (
        <>
          <div className="scanner-frame">
            <div id={containerId} />
            {starting && <div className="scanner-loading">Starting camera…</div>}
          </div>
          <p style={{ color: 'var(--mute)', fontSize: 12, margin: '10px 0 0', textAlign: 'center' }}>
            💡 If you have a USB scanner, you don't need this — just focus the barcode field and scan.
          </p>
        </>
      )}
    </Modal>
  );
};

export default BarcodeScannerModal;
